/**
 * FirstMeeting page - Third step of onboarding
 */

import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '../../components/ui/Button';
import {Input} from '../../components/ui/Input';
import {Container} from '../../components/ui/Container';
import {ProgressBar} from '../../components/onboarding/ProgressBar';
import {useOnboardingStore} from '@voxapilot/shared/stores';
import {ONBOARDING_STEPS} from '@voxapilot/shared/types';
import {ROUTES} from '../../config/constants';
import {api} from '../../config/firebase';

export function FirstMeetingPage() {
  const navigate = useNavigate();
  const {completeStep, updateOnboardingData} = useOnboardingStore();
  
  const [formData, setFormData] = useState({
    title: 'My First Meeting',
    description: 'Getting started with VoxaPilot',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Create meeting via API
      await api.meetings.create({
        title: formData.title,
        description: formData.description,
      });

      updateOnboardingData({firstMeetingCreated: true});
      completeStep(ONBOARDING_STEPS.FIRST_MEETING);
      navigate(ROUTES.ONBOARDING_FEATURES);
    } catch (error) {
      console.error('Failed to create meeting:', error);
      // Still continue even if meeting creation fails
      updateOnboardingData({firstMeetingCreated: true});
      completeStep(ONBOARDING_STEPS.FIRST_MEETING);
      navigate(ROUTES.ONBOARDING_FEATURES);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    completeStep(ONBOARDING_STEPS.FIRST_MEETING);
    navigate(ROUTES.ONBOARDING_FEATURES);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <Container className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="max-w-2xl w-full space-y-8">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar />
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-textPrimary dark:text-gray-100">
                Create Your First Meeting
              </h1>
              <p className="text-textSecondary dark:text-gray-400">
                Let's create a test meeting to get you started
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Meeting Title *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter meeting title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="What's this meeting about?"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-textPrimary dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-orange-500 focus:ring-offset-0"
                  rows={4}
                />
              </div>

              {/* Info box */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <p className="text-sm text-textSecondary dark:text-gray-400">
                  <strong className="text-textPrimary dark:text-gray-100">Tip:</strong> After creating this meeting, 
                  you can upload audio files, record directly, or connect with your calendar to 
                  automatically sync meetings.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isLoading || !formData.title}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? 'Creating...' : 'Create Meeting'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  onClick={handleSkip}
                  className="w-full sm:w-auto"
                >
                  Skip for now
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Container>
    </div>
  );
}

